import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import { ICommonOption, IFilter } from '@/interfaces/common.interface';
import moment from 'moment';
import cookies from 'react-cookies';
import toast from 'react-hot-toast';

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD';

/**
 * common function to set cookies in react application
 * @param name name of cookie
 * @param value value to set in cookie
 */
export const setCookies = (name: string, value: string | object) => {
  cookies.save(name, value, { path: import.meta.env.VITE_PUBLIC_URL });
};

/**
 *
 * @param name find cookies by name
 * @returns
 */
export const getCookies = (name: string) => {
  return cookies.load(name);
};

/**
 *
 * @param name Delete cookies by name
 * @returns
 */
export const deleteCookies = (name: string) => {
  cookies.remove(name, { path: import.meta.env.VITE_BUSINESS });
};

/**
 *
 * @returns reutn the saved auth token
 */
export const getToken = () => {
  return cookies.load(import.meta.env.VITE_BUSINESS + 'token');
};

/**
 * delete the browser data when user logs out
 */
export const logoutUser = async () => {
  try {
    const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.auth}/logout?deviceId=${getDeviceId()}&logoutAll=true`;
    await callApi(PATH, { method: 'DELETE' });
    deleteCookies(import.meta.env.VITE_BUSINESS + 'token');
    deleteCookies(import.meta.env.VITE_BUSINESS + 'refreshToken');
    deleteCookies(import.meta.env.VITE_BUSINESS + 'deviceToken');
    // localStorage.clear();
    setTimeout(() => {
      window.location.href = import.meta.env.VITE_PUBLIC_URL
        ? import.meta.env.VITE_PUBLIC_URL
        : '/';
    }, 1500);
  } catch (error) {
    deleteCookies(import.meta.env.VITE_BUSINESS + 'token');
    deleteCookies(import.meta.env.VITE_BUSINESS + 'refreshToken');
    deleteCookies(import.meta.env.VITE_BUSINESS + 'deviceToken');
    // localStorage.clear();
    setTimeout(() => {
      window.location.href = import.meta.env.VITE_PUBLIC_URL
        ? import.meta.env.VITE_PUBLIC_URL
        : '/';
    }, 1500);
    console.log('Error in logout', error);
  }
};

export function toFixedThree(input: string | number, fixedTo = 3) {
  return parseFloat(input.toString()).toFixed(fixedTo);
}

// export const toFixedThree = (price: any) => {
//   const amount = parseFloat(price);
//   const formattedPrice = new Intl.NumberFormat('en-BH', {
//     style: 'currency',
//     currency: 'BHD',
//     minimumFractionDigits: 3,
//   }).format(amount);
//   return formattedPrice;
// };

/**
 *
 * @param type type of toast notification
 * @param message message to show in toast notification
 */

export const showToastNotification = (
  type: 'success' | 'danger' | 'info' | 'default' | 'warning',
  message: string,
) => {
  const commonOptions = {
    position: 'top-right' as const,
    duration: 5000,
  };

  switch (type) {
    case 'success':
      return toast.success(message, commonOptions);

    case 'danger':
      // react-hot-toast doesn't have "danger" alias — use error
      return toast.error(message, commonOptions);

    case 'warning':
      return toast(message, {
        ...commonOptions,
        icon: '⚠️',
      });

    case 'info':
      // info — use a plain toast or use a custom icon if you want
      return toast(message, commonOptions);

    case 'default':
    default:
      return toast(message, commonOptions);
  }
};

let cachedDeviceId: string | null = null;

export const getDeviceId = (): string => {
  // Return the cached device ID if available
  if (cachedDeviceId !== null) {
    return cachedDeviceId;
  }

  // Attempt to retrieve an existing device ID from localStorage
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    // Use crypto.randomUUID() if available for secure UUID generation
    if (window.crypto && typeof crypto.randomUUID === 'function') {
      deviceId = crypto.randomUUID();
    } else {
      // Fallback: simple UUID generator
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        (c: string): string => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        },
      );
    }
    // Save the generated ID so that it persists across sessions
    localStorage.setItem('deviceId', deviceId);
  }

  // Cache the ID in memory for subsequent call
  cachedDeviceId = deviceId;
  return deviceId;
};

export const statusColors = (status: string) => {
  switch (status) {
    case 'active':
      return 'green-500';
    case 'draft':
      return 'gray-300';
    default:
      return 'gray-500';
  }
};

export enum ProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
  EXPIRED = 'expired',
}

export enum CommonStatus {
  ALL = '',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum SpinWheelTypeEnum {
  LOYALTY_POINTS = 'loyalty_points',
  PRODUCT = 'product',
  GIFT = 'gift',
  COUPON = 'coupon',
  DISCOUNT = 'discount',
}

export const filterOptions: ICommonOption[] = [
  {
    value: CommonStatus.ALL,
    label: 'All',
    textClass: 'text-gray-500',
    bgClass: 'bg-gray-400',
  },
  {
    value: CommonStatus.ACTIVE,
    label: 'Active',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
  },
  {
    value: CommonStatus.INACTIVE,
    label: 'Inactive',
    textClass: 'text-red-500',
    bgClass: 'bg-red-500',
  },
];

export const statusOptions: ICommonOption[] = [
  {
    value: CommonStatus.ACTIVE,
    label: 'Active',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
  },
  {
    value: CommonStatus.INACTIVE,
    label: 'Inactive',
    textClass: 'text-red-500',
    bgClass: 'bg-red-500',
  },
];

export const productOptions: ICommonOption[] = [
  {
    value: ProductStatus.ACTIVE,
    label: 'Active',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
  },
  {
    value: ProductStatus.INACTIVE,
    label: 'Inactive',
    textClass: 'text-red-500',
    bgClass: 'bg-red-500',
  },
  {
    value: ProductStatus.DRAFT,
    label: 'Draft',
    textClass: 'text-yellow-500',
    bgClass: 'bg-yellow-500',
  },
  {
    value: ProductStatus.EXPIRED,
    label: 'Expired',
    textClass: 'text-gray-500',
    bgClass: 'bg-gray-500',
  },
];

export const collectionOption: ICommonOption[] = [
  {
    value: ProductStatus.ACTIVE,
    label: 'Active',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
  },
  {
    value: ProductStatus.INACTIVE,
    label: 'Inactive',
    textClass: 'text-red-500',
    bgClass: 'bg-red-500',
  },
];

export enum OrderStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
  COMPLETED = 'COMPLETED',
  PICKED_UP = 'PICKED_UP',
  IN_PROGRESS = 'IN_PROGRESS',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  DELAYED = 'DELAYED',
  REFUND_REQUESTED = 'REFUND_REQUESTED',
  REFUND_IN_PROGRESS = 'REFUND_IN_PROGRESS',
  REFUNDED = 'REFUNDED',
  REFUND_REJECTED = 'REFUND_REJECTED',
}

export const orderFilterOption = [
  {
    value: '',
    label: 'All',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
  },
  {
    value: OrderStatus.PENDING,
    label: 'Pending',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
  },
  {
    value: OrderStatus.ACCEPTED,
    label: 'Accepted',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-400',
  },
  {
    value: OrderStatus.CANCELED,
    label: 'Canceled',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-400',
  },
  {
    value: OrderStatus.OUT_FOR_DELIVERY,
    label: 'Out For Delivery',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-400',
  },
  {
    value: OrderStatus.DELIVERED,
    label: 'Delivered',
    textClass: 'text-green-700',
    bgClass: 'bg-green-500',
  },
  {
    value: OrderStatus.FAILED,
    label: 'Failed',
    textClass: 'text-green-700',
    bgClass: 'bg-green-500',
  },
  {
    value: 'PAID',
    label: 'Paid',
  },
  {
    value: 'UNPAID',
    label: 'Unpaid',
  },
  {
    value: 'isJunk',
    label: 'Junk Orders',
  },
];

export const orderStatusOptions: ICommonOption[] = [
  {
    value: OrderStatus.PENDING,
    label: 'Pending',
    textClass: 'text-yellow-600',
    bgClass: 'bg-yellow-400',
  },
  {
    value: OrderStatus.ACCEPTED,
    label: 'Accepted',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-400',
  },
  // {
  //   value: OrderStatus.CANCELED,
  //   label: 'Canceled',
  //   textClass: 'text-red-700',
  //   bgClass: 'bg-red-500',
  // },
  // {
  //   value: OrderStatus.FAILED,
  //   label: 'Failed',
  //   textClass: 'text-red-700',
  //   bgClass: 'bg-red-500',
  // },
  // {
  //   value: OrderStatus.COMPLETED,
  //   label: 'Completed',
  //   textClass: 'text-green-700',
  //   bgClass: 'bg-green-500',
  // },
  // {
  //   value: OrderStatus.PICKED_UP,
  //   label: 'Picked Up',
  //   textClass: 'text-indigo-700',
  //   bgClass: 'bg-indigo-500',
  // },
  // {
  //   value: OrderStatus.IN_PROGRESS,
  //   label: 'In Progress',
  //   textClass: 'text-purple-700',
  //   bgClass: 'bg-purple-500',
  // },
  // {
  //   value: OrderStatus.OUT_FOR_DELIVERY,
  //   label: 'Out for Delivery',
  //   textClass: 'text-teal-700',
  //   bgClass: 'bg-teal-500',
  // },
  {
    value: OrderStatus.DELIVERED,
    label: 'Delivered',
    textClass: 'text-green-700',
    bgClass: 'bg-green-500',
  },
  // {
  //   value: OrderStatus.DELAYED,
  //   label: 'Delayed',
  //   textClass: 'text-orange-700',
  //   bgClass: 'bg-orange-500',
  // },
  // {
  //   value: OrderStatus.REFUND_REQUESTED,
  //   label: 'Refund Requested',
  //   textClass: 'text-pink-700',
  //   bgClass: 'bg-pink-500',
  // },
  // {
  //   value: OrderStatus.REFUND_IN_PROGRESS,
  //   label: 'Refund in Progress',
  //   textClass: 'text-pink-700',
  //   bgClass: 'bg-pink-400',
  // },
  // {
  //   value: OrderStatus.REFUNDED,
  //   label: 'Refunded',
  //   textClass: 'text-pink-800',
  //   bgClass: 'bg-pink-600',
  // },
  // {
  //   value: OrderStatus.REFUND_REJECTED,
  //   label: 'Refund Rejected',
  //   textClass: 'text-red-800',
  //   bgClass: 'bg-red-600',
  // },
];

export const formatDateTime = (
  date: string | null | undefined | Date,
  type = 'default',
): string => {
  if (!date) {
    return '';
  }
  const formatedDate: Record<string, string> = {
    default: moment(date).format('MMM D, h:mm a'),
    YYMMDD: moment(date).format('YYYY-MM-DD'),
    YYMMMDDHH: moment(date).format('DD MMM YYYY, h:mm a'),
    YYMMMDD: moment(date).format('DD MMM YYYY'),
  };

  return formatedDate[type] || '';
};

export const formatDeliveryRange = (range?: string): string => {
  if (!range) return '';

  const [startRaw, endRaw] = range.split(' - ');

  if (!startRaw || !endRaw) return range;

  const start = moment(startRaw, 'YYYY-MM-DD hh:mm A');
  if (!start.isValid()) return range;

  const end = moment(
    `${start.format('YYYY-MM-DD')} ${endRaw}`,
    'YYYY-MM-DD hh:mm A',
  );

  return `${start.format('DD MMM YYYY, h:mm A')} - ${end.format('h:mm A')}`;
};

export const GetFilterUrl = (filter: IFilter) => {
  return filter
    ? Object?.entries(filter)
        ?.filter(([, value]) => value)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join('&')
    : '';
};

export const checkIsExpire = (
  date: string | Date | null | undefined,
): boolean => {
  if (date) {
    const today = moment();
    const inputDate = moment(date);
    return inputDate.isAfter(today);
  } else {
    return false;
  }
};

export const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export function formatStatus(status: string | undefined): string {
  return (
    status ||
    ''
      .toLowerCase() // convert to lowercase
      .replace(/_/g, ' ') // replace underscores with spaces
      .replace(/^\w/, (c) => c.toUpperCase()) // capitalize the first letter
      .replace(/\bof\b/g, 'of')
  ); // keep 'of' lowercase
}

export const getPaymentMethodMessage = (
  method: string,
  paymentStatus?: string,
): string => {
  const paymentMethodMap: Record<string, string> = {
    cod: 'Cash on Delivery',
    wallet: paymentStatus === 'PAID' ? 'Paid via Wallet' : 'Nass Wallet',
    card: paymentStatus === 'PAID' ? 'Paid via Credit Card' : 'Credit Card',
    apple_pay: paymentStatus === 'PAID' ? 'Paid via Apple Pay' : 'Apple Pay',
    benefitpay: paymentStatus === 'PAID' ? 'Paid via BenefitPay' : 'BenefitPay',
    'bh.benefit':
      paymentStatus === 'PAID' ? 'Paid via Debit Card' : 'Debit Card',
  };

  const key = method.toLowerCase();
  return paymentMethodMap[key] || 'Unknown Payment Method';
};

export const getUserType = (type: string, isEmployee = false): string => {
  if (isEmployee) {
    return 'Employee';
  }
  const userTypes: Record<string, string> = {
    user: 'User',
    driver: 'Driver',
    employee: 'Employee',
  };
  const key = type && type.toLowerCase();
  return userTypes[key] || type || 'unkown type';
};

export const RolesType = [
  'profile',
  'cart',
  'address',
  'product',
  'collection',
  'category',
  'rating',
  'user',
  'role',
  'order',
  'brand',
  'discount',
  'daily spin',
  'website content',
  'settings',
];
export const RolesPermission = ['add', 'view', 'update', 'remove'];
const customActions: Record<string, string[]> = {
  product: RolesPermission.filter((action) => action !== 'add'),
  brand: RolesPermission.filter((action) => action !== 'add'),
  category: RolesPermission.filter((action) => action !== 'add'),
};
export const RolesTypes = RolesType.map((resource) => ({
  resource,
  actions: customActions[resource] || RolesPermission,
}));

export const formatPlaceholder = (type: string | undefined | null) => {
  const types: Record<string, string> = {
    day: 'Today',
    month: 'This Month',
    week: 'This Week',
    Year: 'This Year',
  };
  if (type) {
    return types[type];
  } else {
    return '';
  }
};

export const getStatusWarning = (type: string) => {
  const message: Record<string, string> = {
    product:
      'The collections related to this product are also going to be inactive',
    category:
      'The product related to this category and collections related to these product also going to be inactive',
    brand: 'The product related to this brand will also going to be inactive.',
  };
  return message[type];
};

export const parseToJson = <T = any>(data: string | object | []): T => {
  try {
    if (typeof data === 'string') {
      return JSON.parse(data) as T;
    } else {
      return data as T;
    }
  } catch (err) {
    console.error('Invalid JSON:', err);
    return [] as unknown as T; // fallback if parsing fails
  }
};

export const ThrowDateTimeError = (startDate: Date, endDate: Date) => {
  let message: string | null;
  // const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (end < start) {
    message = 'End date cannot be earlier than start date.';
  } else if (end.getTime() === start.getTime()) {
    message = 'Start date and end date cannot be the same.';
  }
  // else if (start < today) {
  //   message = 'Start date must be today or a future date.';
  // }
  else {
    message = null;
  }

  return message;
};

export const getLocalItem = (key: string) => {
  const val = localStorage.getItem(key);

  return val;
};

export const setLocalItem = (key: string, vlaue: string) => {
  localStorage.setItem(key, vlaue);
  return vlaue;
};
